import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { apiClient } from "@surgia/api/src/client";
import moment from "moment";

//containers
import TenantsTable from "../../containers/tables/tenants-table";
import CreateTenantForm from "../../containers/tables/createTenantForm";
import RequestTable from "../../containers/tables/requests-table";
import AssignBranchForm from "@surgia/ui/containers/assignBranchForm";
//components
import { GenericTableHeader } from "@surgia/ui";
import Button from "@surgia/ui/components/button";
import TenantInfoCard from "@surgia/ui/components/tenantInfoCard";
import GenericStepper from "@surgia/ui/components/genericModal/steps";
import TableControls from "@surgia/ui/containers/table/table-controls";
import GenericModal from "@surgia/ui/components/genericModal";
import ConfirmationDialog from "@surgia/ui/rubbers/ConfirmationDialog";
import HeaderLayout from "@surgia/ui/layouts/Header";
import ListboxDropdown from "@surgia/ui/components/ListboxDropdown";
import { Input } from "@headlessui/react";
//queries
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import { requestQueries } from "@surgia/api/src/queryStore/admin/request";
import { useGetTenantRequests } from "../../hooks/quries/useGetRequests";
import { useGetTenants } from "../../hooks/quries/useGetTenants";
//icons
import SuccessIcon from "@surgia/ui/icons/popup/success.svg";
import ActivationSuccessIcon from "@surgia/ui/icons/popup/activation.svg";
//constants
const ITEMS_PER_PAGE = 8;
import { handleViewDetails } from "./helpers";

//move it to separate file
const ActivationSuccess = ({ onConfirm }: { onConfirm: () => void }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  return (
    <div
      className="flex flex-col items-center justify-center"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="p-4 mb-4">
        <img
          src={ActivationSuccessIcon}
          alt={t("shared.tenantActivation.successTitle")}
        />
      </div>
      <h2 className="text-[18px] font-bold text-center text-[#181D27]">
        {t("shared.tenantActivation.successTitle")}
      </h2>
      <p className="table-row-sub text-center mb-6">
        {t("shared.tenantActivation.successDescription")}
      </p>
      <Button
        variant="primary"
        onClick={onConfirm}
        className="w-full"
        size="lg"
      >
        {t("shared.tenantActivation.confirm")}
      </Button>
    </div>
  );
};

//move it to separate file
const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
  cities,
  types,
  isCitiesLoading,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
  cities: { id: number | string; name: string }[];
  types: { value: string; label: string }[];
  isCitiesLoading: boolean;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      id: "",
      city: "",
      type: "",
      created_at_from: "",
      created_at_to: "",
    });
  };

  // Find selected city and type objects
  const selectedCity =
    cities.find((city) => city.id.toString() === localFilters.city) || null;
  const selectedType =
    types.find((type) => type.value === localFilters.type) || null;

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
      additionalStyles="!overflow-visible !w-[500px]"
    >
      <div className="space-y-4">
        {/* ID */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>

        {/* City - ListboxDropdown */}
        <ListboxDropdown
          options={cities}
          value={selectedCity}
          onChange={(selectedCity) => {
            setLocalFilters({
              ...localFilters,
              city: (selectedCity as any)?.id?.toString() || "",
            });
          }}
          label={t("shared.table.city")}
          placeholder={t("admin.selectCity")}
          includeSelectOption={true}
          selectOptionText={t("admin.selectCity")}
          loading={isCitiesLoading}
          loadingText={t("admin.loadingCities")}
          emptyText={t("admin.noCitiesAvailable")}
          className="mb-0"
        />

        {/* Type - ListboxDropdown */}
        <ListboxDropdown
          options={types.map((type) => ({ id: type.value, name: type.label }))}
          value={
            selectedType
              ? { id: selectedType.value, name: selectedType.label }
              : null
          }
          onChange={(selectedType) => {
            setLocalFilters({
              ...localFilters,
              type: (selectedType as any)?.id?.toString() || "",
            });
          }}
          label={t("shared.table.type")}
          placeholder={t("admin.selectType")}
          includeSelectOption={true}
          selectOptionText={t("admin.selectType")}
          className="mb-0"
        />

        {/* Date Range Filters */}
        <div className="grid grid-cols-2 gap-4">
          {/* Start Date */}
          <div>
            <label className="block text-[14px] mb-1">Start Date</label>
            <Input
              type="date"
              className="w-full h-11 px-3.5 py-2.5 bg-white border border-[#D5D7DA] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[#181D27]"
              value={
                localFilters.created_at_from
                  ? moment(localFilters.created_at_from)
                      .format("YYYY-MM-DD")
                      ?.replace("00:00:00", "23:59:59")
                  : ""
              }
              max={
                localFilters.created_at_to
                  ? moment(localFilters.created_at_to).format("YYYY-MM-DD")
                  : undefined
              }
              onChange={(e) => {
                const value = e.target.value;
                setLocalFilters({
                  ...localFilters,
                  created_at_from: value
                    ? moment(value)
                        .format("YYYY-MM-DDTHH:mm:ss")
                        ?.replace("00:00:00", "23:59:59")
                    : "",
                });
              }}
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-[14px] mb-1">End Date</label>
            <Input
              type="date"
              className="w-full h-11 px-3.5 py-2.5 bg-white border border-[#D5D7DA] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[#181D27]"
              value={
                localFilters.created_at_to
                  ? moment(localFilters.created_at_to).format("YYYY-MM-DD")
                  : ""
              }
              min={
                localFilters.created_at_from
                  ? moment(localFilters.created_at_from).format("YYYY-MM-DD")
                  : undefined
              }
              onChange={(e) => {
                const value = e.target.value;
                setLocalFilters({
                  ...localFilters,
                  created_at_to: value
                    ? moment(value)
                        .format("YYYY-MM-DDTHH:mm:ss")
                        .replace("00:00:00", "23:59:59")
                    : "",
                });
              }}
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

//move it to separate file
const FilterSection: React.FC<{
  openFilters: boolean;
  setOpenFilters: (open: boolean) => void;
  filters: any;
  setFilters: (f: any) => void;
  citiesLookup: any;
  isCitiesLoading: boolean;
}> = ({
  openFilters,
  setOpenFilters,
  filters,
  setFilters,
  citiesLookup,
  isCitiesLoading,
}) => {
  const { t } = useTranslation();
  return (
    <FiltersModal
      open={openFilters}
      onClose={() => setOpenFilters(false)}
      onApply={() => setOpenFilters(false)}
      filters={filters}
      setFilters={setFilters}
      cities={citiesLookup?.data || []}
      types={[
        { value: "HOSPITAL", label: t("shared.entityType.HOSPITAL") },
        { value: "POLY_CLINIC", label: t("shared.entityType.POLY_CLINIC") },
        { value: "OTHER", label: t("shared.entityType.OTHER") },
      ]}
      isCitiesLoading={isCitiesLoading}
    />
  );
};

//move it to separate file
const TenantCreationSection: React.FC<{
  openCreateTenant: boolean;
  setOpenCreateTenant: (open: boolean) => void;
  citiesLookup: any;
}> = ({ openCreateTenant, setOpenCreateTenant, citiesLookup }) => {
  const { t } = useTranslation();
  return (
    <GenericModal
      title={t("admin.createTenant")}
      open={openCreateTenant}
      onClose={() => setOpenCreateTenant(false)}
      footer={
        <div className="flex justify-between gap-4 w-full">
          <Button
            variant="outline"
            onClick={() => setOpenCreateTenant(false)}
            className="w-full"
          >
            {t("Cancel")}
          </Button>
          <Button variant="primary" onClick={() => {}} className="w-full">
            {t("Create tenant")}
          </Button>
        </div>
      }
    >
      <CreateTenantForm cities={citiesLookup?.data || []} />
    </GenericModal>
  );
};

//move it to separate file
const ActivationModal: React.FC<{
  showActivationModal: boolean;
  setShowActivationModal: (show: boolean) => void;
  activationStep: number;
  setActivationStep: (step: number) => void;
  requestId: number | undefined;
  activateRequestMutation: any;
  countryId: number;
}> = ({
  showActivationModal,
  setShowActivationModal,
  activationStep,
  setActivationStep,
  requestId,
  activateRequestMutation,
  countryId,
  }) => {
    const { t } = useTranslation();

    return (
    <GenericModal
      title={t("shared.tenantActivation.modalTitle")}
      subtitle={t("shared.tenantActivation.modalSubtitle")}
      open={showActivationModal}
      onClose={() => setShowActivationModal(false)}
      stepper={
        <GenericStepper
          steps={[
            { label: t("shared.tenantActivation.stepReviewInfo") },
            { label: t("shared.tenantActivation.stepAssignBranch") },
            { label: t("shared.tenantActivation.stepConfirmActivate") },
          ]}
          currentStep={activationStep}
        />
      }
      footer={
        activationStep === 0 && (
          <>
            <Button
              variant="outline"
              onClick={() => setShowActivationModal(false)}
              className="w-full text-[16px]"
              size="lg"
            >
              {t("shared.tenantActivation.cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={activationStep > 1}
              onClick={() => setActivationStep(activationStep + 1)}
              className="w-full text-[16px]"
            >
              {t("shared.tenantActivation.next")}
            </Button>
          </>
        )
      }
    >
      {activationStep === 0 && (
        <TenantInfoCard requestId={requestId?.toString()} />
      )}
      {activationStep === 1 && (
        <AssignBranchForm
          onSuccess={() => setActivationStep(activationStep + 1)}
          requestId={requestId}
          onClose={() => setActivationStep(activationStep - 1)}
          countryId={countryId}
        />
      )}
      {activationStep === 2 && (
        <ActivationSuccess
          onConfirm={() => {
            activateRequestMutation.mutate(requestId!);
            setShowActivationModal(false);
          }}
        />
      )}
    </GenericModal>
  );
};

//move it to separate file

const Tenants: React.FC = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("ACTIVE");
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activationStep, setActivationStep] = useState(0);
  const [requestId, setRequestId] = useState<number | undefined>(undefined);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [countryId, setCountryId] = useState<number | undefined>(undefined);
  const [filters, setFilters] = useState({
    id: "",
    city: "",
    type: "",
    created_at_from: "",
    created_at_to: "",
  });
  const [openCreateTenant, setOpenCreateTenant] = useState(false);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "createdAt", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  //queries_hooks
  const { data: tenantResponse, isLoading: isTenantLoading } = useGetTenants({
    currentPage,
    search,
    filters,
    size: ITEMS_PER_PAGE,
    sort:
      sortState.column && sortState.direction && status === "ACTIVE"
        ? `${sortState.column},${sortState.direction}`
        : undefined,
    enabled: status === "ACTIVE",
  });
  const { data: requestsResponse, isLoading: isRequestsLoading } =
    useGetTenantRequests({
      currentPage,
      search,
      filters,
      size: ITEMS_PER_PAGE,
      sort:
        sortState.column && sortState.direction && status === "PENDING"
          ? `${sortState.column},${sortState.direction}`
          : undefined,
      enabled: status === "PENDING",
    });
  //normal queries
  const { data: citiesLookup, isLoading: isCitiesLoading } = useQuery(
    lookupQueries.queries.cities({ size: 100, country_id: 191 })
  );

  //calculate total count and total pages
  const totalCount = useMemo(() => {
    if (status === "PENDING") {
      return requestsResponse?.total || 0;
    } else {
      return tenantResponse?.total || 0;
    }
  }, [status, requestsResponse?.total, tenantResponse?.total]);
  const totalPages = useMemo(() => {
    if (status === "PENDING") {
      return requestsResponse?.totalPages || 1;
    } else {
      return tenantResponse?.totalPages || 1;
    }
  }, [status, requestsResponse?.totalPages, tenantResponse?.totalPages]);

  const handleActivate = (id: string) => {
    setIsDialogOpen(true);
    setRequestId(parseInt(id));
  };

  const handleActivateTenant = async () => {
    // Fetch request details to check if branches exist
    if (requestId) {
      try {
        const response = await apiClient.get("/corporate/v1/request", {
          params: { id: requestId },
        });
        const requestData = response.data[0];

        // Extract country_id from request data - must exist
        if (!requestData?.country?.id) {
          console.error("Country ID not found in request data");
          toast.error(t("admin.toasters.tenantActivationFailed"), {
            description: "Country information is missing",
          });
          setIsDialogOpen(false);
          return; // Don't open modal if country_id is missing
        }

        const countryIdFromRequest = requestData.country.id;
        setCountryId(countryIdFromRequest);

        // Check if branches exist and set step accordingly
        if (requestData?.branches && requestData.branches.length > 0) {
          setActivationStep(2); // Skip to confirmation step if branches exist
        } else {
          setActivationStep(0); // Start from review step if no branches
        }

        setShowActivationModal(true);
        setIsDialogOpen(false);
      } catch (error) {
        console.error("Error fetching request details:", error);
        toast.error(t("admin.toasters.tenantActivationFailed"), {
          description: t("admin.toasters.tryAgain"),
        });
        setIsDialogOpen(false);
      }
    }
  };

  const activateRequestMutation = useMutation({
    mutationFn: (requestId: number) =>
      requestQueries.mutations.activate(requestId).mutationFn(),
    onSuccess: () => {
      // Invalidate and refetch requests data
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("tenants");
        },
      });
      // Close the modal
      toast.success(t("admin.toasters.tenantActicated"), {});
      setShowActivationModal(false);
      setActivationStep(0);
    },
    onError: () => {
      toast.error(t("admin.toasters.tenantActivationFailed"), {
        description: t("admin.toasters.tryAgain"),
      });
    },
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [
    status,
    filters.city,
    filters.id,
    filters.type,
    filters.created_at_from,
    filters.created_at_to,
  ]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const statusTabs = [
    { label: t("shared.tableToolbar.active"), value: "ACTIVE" },
    { label: t("shared.tableToolbar.pending"), value: "PENDING" },
  ];

  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      <HeaderLayout
        title={t("admin.tenants")}
        subtitle={t("admin.tenantsSubtitle")}
      />

      <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
        <GenericTableHeader
          titleTranslationKey="admin.tenants"
          totalCount={totalCount}
          status={status}
          singularLabel="tenant"
          pluralLabel="tenants"
        />
        <TableControls
          status={status}
          setStatus={setStatus}
          search={search}
          setSearch={setSearch}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          statusTabs={statusTabs}
        />

        {/* Table Section */}
        {status === "PENDING" ? (
          <RequestTable
            data={requestsResponse?.data}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onActivate={handleActivate}
            sortState={sortState}
            onSort={handleSort}
          />
        ) : (
          <TenantsTable
            data={tenantResponse?.data}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onActivate={handleActivate}
            onViewDetails={handleViewDetails}
            loading={isTenantLoading || isRequestsLoading}
            sortState={sortState}
            onSort={handleSort}
          />
        )}
      </div>

      {isDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30 backdrop-blur-sm">
          <ConfirmationDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            onConfirm={handleActivateTenant}
            title={t("shared.tenantActivation.activateNow")}
            description={t("shared.tenantActivation.activateNowDescription")}
            confirmLabel={t("shared.tenantActivation.activateNowLabel")}
            cancelLabel={t("shared.tenantActivation.notNowLabel")}
            icon={<img src={SuccessIcon} alt="Success" />}
            additionalStyles="w-fit h-fit bg-white shadow-xl  rounded-xl"
          />
        </div>
      )}

      {showActivationModal && countryId && (
        <ActivationModal
          showActivationModal={showActivationModal}
          setShowActivationModal={setShowActivationModal}
          activationStep={activationStep}
          setActivationStep={setActivationStep}
          requestId={requestId}
          activateRequestMutation={activateRequestMutation}
          countryId={countryId}
        />
      )}

      {openFilters && (
        <FilterSection
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          filters={filters}
          setFilters={setFilters}
          citiesLookup={citiesLookup}
          isCitiesLoading={isCitiesLoading}
        />
      )}

      {openCreateTenant && (
        <TenantCreationSection
          openCreateTenant={openCreateTenant}
          setOpenCreateTenant={setOpenCreateTenant}
          citiesLookup={citiesLookup}
        />
      )}
    </div>
  );
};

export default Tenants;
